import { Box, Button, Flex, Heading, Text } from "@chakra-ui/react";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { SelectControl } from "formik-chakra-ui";
import _ from "lodash";
import React, { useMemo } from "react";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import { Subgrid, SubgridLayout } from "src/components/Layout/Subgrid";
import { AvelaToast, useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { validateWithZod } from "src/services/formValidations";
import { isNotNull } from "src/services/predicates";
import { assertEnum } from "src/types/assert";
import * as GQL from "src/types/graphql";
import * as LegacyRules from "./legacyRules";
import { UPDATE_LEGACY_FORM_TEMPLATE_RULE } from "./mutations";
import { NextStepForm } from "./NextStepForm";

type FormikType = LegacyRules.FormikType;

const PANEL_FORM_ID = "form-templates-rule-panel-form";

interface RuleFormPanelProps {
  isOpen: boolean;
  onClose: () => void;
  initialValues: FormikType;
  onSubmit: (
    values: FormikType,
    formikHelpers: FormikHelpers<FormikType>
  ) => Promise<void>;
  schools: { id: string; name: string }[];
  formTemplates: { id: string; name: string }[];
  formId: string;
}

export const RuleConnectFormPanel: React.FC<RuleFormPanelProps> = ({
  isOpen,
  onClose,
  initialValues,
  onSubmit,
  schools,
  formTemplates,
  formId,
}) => {
  const toast = useAvelaToast();

  // Determine if we're editing or creating a rule
  const isEditMode = useMemo(() => {
    // If the rule has a trigger status and next form template ID filled in, it's an existing rule
    return Boolean(
      initialValues.trigger?.status && initialValues.action?.nextFormTemplateId
    );
  }, [initialValues]);

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "You have unsaved changes",
    body: "Are you sure you want to cancel?",
  });

  return (
    <Formik<FormikType>
      initialValues={initialValues}
      enableReinitialize
      validate={validateWithZod(LegacyRules.FormikSchema)}
      onSubmit={async (values, formikHelpers) => {
        try {
          await onSubmit(values, formikHelpers);
          onClose();
        } catch (error) {
          console.error(error);
          toast.error({
            title: "Error",
            description: "Failed to save rule",
          });
        }
      }}
    >
      {(formik) => {
        // need to omit lexical fields for dirty calculation since we always dynamnically populate those fields on form load
        const isDirty = !_.isEqual(formik.initialValues, {
          ...formik.values,
          nextStep: _.omit(formik.values.nextStep, [
            "titleLexical",
            "subtitleLexical",
            "bodyLexical",
            "buttonTextLexical",
          ]),
        });
        const closeWithConfirmation = async () => {
          if (!isDirty) {
            onClose();
            return;
          }

          if (await confirm()) {
            onClose();
            formik.resetForm();
          }
        };
        return (
          <ResizableDrawer
            isOpen={isOpen}
            placement="right"
            onClose={closeWithConfirmation}
            showCloseButton={false}
            closeOnOverlayClick={false}
            size="lg"
            header={isEditMode ? "Edit rule" : "Create rule"}
            footer={
              <>
                <Button
                  variant="outline"
                  colorScheme="gray"
                  mr={3}
                  onClick={closeWithConfirmation}
                  width="50%"
                >
                  Cancel
                </Button>
                <Button type="submit" form={formId} width="50%">
                  Save
                </Button>
              </>
            }
            autosaveId="rule-form-panel-width"
          >
            <Flex
              direction="column"
              as={Form}
              id={formId}
              gap="6"
              marginTop={4}
            >
              <Flex direction="column" gap="4">
                <Box
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                >
                  <When />
                  <Where schools={schools} />
                  <Then formTemplates={formTemplates} />
                </Box>
                <NextStepForm />
                {confirmationDialog}
              </Flex>
            </Flex>
          </ResizableDrawer>
        );
      }}
    </Formik>
  );
};

const When = () => {
  const formik = useFormikContext<LegacyRules.FormikType>();
  const triggerType = formik.values.trigger.type;
  return (
    <Flex
      direction="column"
      padding={4}
      gap="2"
      borderBottom="1px solid"
      borderColor="gray.200"
    >
      <Heading as="h3" fontSize="md" fontWeight="semibold">
        When
      </Heading>
      <SubgridLayout
        columns={[
          ["col-1", "1fr"],
          ["col-2", "auto"],
          ["col-3", "1fr"],
        ]}
        rows={[
          ["label", "auto"],
          ["input", "auto"],
          ["error", "auto"],
        ]}
        gap="0"
        columnGap="4"
      >
        <SelectControl
          name="trigger.type"
          as={Subgrid}
          label="Select trigger"
          selectProps={{ placeholder: "Select an option" }}
          gridColumn={
            triggerType === GQL.rule_type_enum.StatusRule ? undefined : "span 3"
          }
        >
          <option value={GQL.rule_type_enum.StatusRule}>
            Status or sub-status
          </option>
        </SelectControl>
        {triggerType === GQL.rule_type_enum.StatusRule && (
          <>
            <Subgrid gridAutoColumns="min-content" alignItems="center">
              <Text gridRowStart="input">is</Text>
            </Subgrid>
            <StatusSelectControl name="trigger.status" />
          </>
        )}
      </SubgridLayout>
    </Flex>
  );
};

type School = { id: string; name: string };
const Where = (props: { schools: School[] }) => {
  const { schools } = props;
  const formik = useFormikContext<LegacyRules.FormikType>();
  const rule = formik.values;
  const selectedSchools =
    rule?.trigger.schoolIds
      .map((id) => {
        const school = schools.find((s) => s.id === id);
        if (!school) {
          return null;
        }
        return {
          id: school.id,
          label: school.name,
        };
      })
      .filter(isNotNull) ?? [];

  const triggerType = rule?.trigger.type;
  if (triggerType !== GQL.rule_type_enum.StatusRule) {
    return null;
  }

  return (
    <Flex
      direction="column"
      padding="4"
      gap="2"
      borderBottom="1px solid"
      borderColor="gray.200"
    >
      <Heading as="h3" fontSize="md" fontWeight="semibold">
        Where
      </Heading>
      <Flex direction="column" gap="4">
        <SubgridLayout
          columnGap="4"
          columns={[
            ["col-1", "1fr"],
            ["col-2", "auto"],
            ["col-3", "1fr"],
          ]}
          rows={[
            ["label", "auto"],
            ["input", "auto"],
            ["error", "auto"],
          ]}
        >
          <SelectControl
            labelProps={{
              display: "flex",
              alignItems: "end",
            }}
            as={Subgrid}
            name="schoolCondition"
            label="Select condition"
          >
            <option key="schools">Schools</option>
          </SelectControl>
          <Subgrid
            gridAutoColumns="min-content"
            alignItems="center"
            gridColumnStart="col-2"
            gridRowStart="input"
          >
            <Text>are</Text>
          </Subgrid>
          <SelectControl
            as={Subgrid}
            gridColumnStart="col-3"
            gridRowStart="input"
            labelProps={{
              display: "flex",
              alignItems: "end",
            }}
            name={`trigger.schoolIdsOperator`}
          >
            <option value={GQL.list_operator_enum.any}>Any schools</option>
            <option value={GQL.list_operator_enum.in}>Specific schools</option>
          </SelectControl>
        </SubgridLayout>
        {formik.values.trigger.schoolIdsOperator !==
          GQL.list_operator_enum.any && (
          <MultiSelectInput
            label="Select schools"
            size="md"
            options={schools.map((s) => ({
              id: s.id,
              label: s.name,
            }))}
            initialValues={selectedSchools}
            onChange={(schools) => {
              formik.setFieldValue(
                "trigger.schoolIds",
                schools.map((s) => s.id)
              );
            }}
            placeholder="Select schools"
            renderDropdownItem={(item) => {
              return <Flex key={item.id}>{item.label}</Flex>;
            }}
          />
        )}
      </Flex>
    </Flex>
  );
};

type FormTemplate = { id: string; name: string };
const Then = (props: { formTemplates: FormTemplate[] }) => {
  const { formTemplates } = props;
  const formik = useFormikContext<LegacyRules.FormikType>();
  const actionType = formik.values.action.type;
  return (
    <Flex
      direction="column"
      bg="gray.50"
      borderRadius="md"
      borderTopRadius="0"
      padding={4}
      gap="2"
    >
      <Heading as="h3" fontSize="md" fontWeight="semibold">
        Then
      </Heading>
      <SubgridLayout
        columns={[
          ["col-1", "1fr"],
          ["col-2", "1fr"],
        ]}
        rows={[
          ["label", "auto"],
          ["input", "auto"],
          ["error", "auto"],
        ]}
        columnGap="2"
      >
        <SelectControl
          as={Subgrid}
          name={`action.type`}
          label="Select action"
          selectProps={{ placeholder: "Select an option" }}
          gridColumn={actionType === "AssignForm" ? undefined : "span 2"}
        >
          <option value="AssignForm">Continue to another form</option>
        </SelectControl>
        {actionType === "AssignForm" && (
          <SelectControl
            as={Subgrid}
            gridColumnStart="col-2"
            name={`action.nextFormTemplateId`}
            label="Select form to follow"
            selectProps={{ placeholder: "Select an option" }}
          >
            {formTemplates.map((ft) => {
              return (
                <option key={ft.id} value={ft.id}>
                  {ft.name}
                </option>
              );
            })}
          </SelectControl>
        )}
      </SubgridLayout>
    </Flex>
  );
};

const StatusSelectControl = (props: { name: string }) => {
  const { name } = props;
  return (
    <SelectControl
      as={Subgrid}
      name={name}
      label="Select status or sub-status"
      selectProps={{ placeholder: "Select an option" }}
    >
      {Object.values(GQL.form_status_enum).map((value) => (
        <Box key={value} as="option" value={value}>
          Form{" "}
          {value === GQL.form_status_enum.Cancelled
            ? "canceled"
            : value?.toLowerCase()}
        </Box>
      ))}
      {Object.values(GQL.offer_status_enum).map((value) => (
        <Box key={value} as="option" value={value}>
          Offer {value?.toLowerCase()}
        </Box>
      ))}
      {Object.values(GQL.waitlist_status_enum).map((value) => (
        <Box key={value} as="option" value={value}>
          Waitlist {value?.toLowerCase()}
        </Box>
      ))}
    </SelectControl>
  );
};

export type Props = {
  toast: AvelaToast;
  legacyRule: LegacyRules.FormikType | null;
  formTemplate: GQL.EditFormTemplateFragment;
  formTemplates: GQL.GetLegacyFormTemplateRuleByFormTemplateId_form_template[];
  schools: GQL.GetLegacyFormTemplateRuleByFormTemplateId_school[];
  newRule: LegacyRules.FormikType | null;
  isOpen: boolean;
  deleteLegacyRules: (
    variables: GQL.DeleteLegacyFormTemplateRuleVariables
  ) => Promise<void>;
  setIsEditingLegacyRule: (state: boolean) => void;
  refetchLegacyRules: () => Promise<void>;
  onClose: () => void;
};
export const LegacyRuleDrawer: React.FC<Props> = ({
  toast,
  legacyRule,
  formTemplate,
  formTemplates,
  schools,
  newRule,
  isOpen,
  deleteLegacyRules,
  setIsEditingLegacyRule,
  refetchLegacyRules,
  onClose,
}) => {
  const [updateLegacyFormTempalateRule] = useRemoteDataMutation<
    GQL.UpdateLegacyFormTemplateRule,
    GQL.UpdateLegacyFormTemplateRuleVariables
  >(UPDATE_LEGACY_FORM_TEMPLATE_RULE);

  const handleSubmit = async (
    rule: FormikType,
    formik: FormikHelpers<FormikType>
  ) => {
    try {
      if (rule) {
        await updateLegacyFormTempalateRule({
          variables: {
            formTemplateId: formTemplate.id,
            formTemplateRuleId: rule.id,
            formTemplateRule: {
              id: rule.id,
              operator: GQL.operator_enum.or,
            },
            rule: {
              id: rule.trigger.id,
              form_template_rule_id: rule.id,
              type: assertEnum(GQL.rule_type_enum, rule.trigger.type),
            },
            statusRule: LegacyRules.toGQLStatusRule(rule),
            schools:
              rule.trigger.schoolIdsOperator === GQL.list_operator_enum.any
                ? []
                : rule.trigger.schoolIds.map((schoolId) => ({
                    rule_id: rule.trigger.id,
                    school_id: schoolId,
                  })),
          },
        });
      }

      formik.resetForm({ values: rule });
      toast({
        title: "Success",
        description: "Rules updated",
        status: "success",
      });
      setIsEditingLegacyRule(false);

      // Refetch data to update the view
      await refetchLegacyRules();
    } catch (error) {
      console.error(error);
      toast.error({
        title: "Oops! Something went wrong",
      });
    }
  };

  const initialValues = newRule || legacyRule;
  if (initialValues === null) return null;
  return (
    <RuleConnectFormPanel
      isOpen={isOpen}
      onClose={onClose}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      schools={schools}
      formTemplates={formTemplates}
      formId={PANEL_FORM_ID}
    />
  );
};
